import { useOrder, useWarrantyData } from "app/hooks";
import { useWarrantyOrderVoucherData } from "app/hooks/payment/useWarrantyOrderVoucherData";
import { usePaymentContext } from "pages/payment/providers/PaymentProvider";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryGates } from "app/hooks/shared/useQueryGates";
import {
  DiscountRecords,
  GetOrderResponse,
  WarrantyPackage,
  WarrantyPackagesResponse,
} from "shared/api/middleware-api-client";
import { APPOINTMENT_TYPE_SEARCH_PARAM } from "shared/config";
import { DELIVERY_PRICES } from "shared/config/pricing";
import { voucherState } from "shared/store/voucher/voucherStore";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useSnapshot } from "valtio";

const clamp = require("lodash.clamp");
export interface OrderProviderProps {
  type: string;
  order: GetOrderResponse | undefined;
  orderReference: string;
  orderError: any;
  isOrderFetching: boolean;
  orderId: string | null;
  warrantyPackage: WarrantyPackage | undefined;
  monthlyPayment: number;
  remaining: string;
  warrantyDiscount: number;
  total: number;
  orderStatusId: number;
  isOrderAssembly?: boolean;

  setOrderReference: React.Dispatch<React.SetStateAction<string>>;
  refetchOrder: () => Promise<any>;
  setWarrantyPackage: React.Dispatch<
    React.SetStateAction<WarrantyPackage | undefined>
  >;
  getFormattedSubTotalPrice: () => string;
  getFormattedWarrantyPackagePrice: () => string;
  getFormattedAbsoluteDiscountPrice: () => string;
  getTotalPrice: (paymentMethod?: string) => string;
  getRawTotalPrice: () => number;
  setMonthlyPayment: React.Dispatch<React.SetStateAction<number>>;
  getOrderSubtotal: () => number;
  getWarrantyPackageTotal: () => number;
  getRawOrderDiscountRecordsPrice: () => number;
  setRemaining: React.Dispatch<React.SetStateAction<string>>;
  getOrderTotalRemaining: () => number;
  getOrderTotalPaid: () => number;
  setWarrantyDiscount: React.Dispatch<React.SetStateAction<number>>;
  getWarrantyPackageDiscount: (method: string) => number;
  getWarrantyOwnRiskTotal: () => number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  getOrderWarrantyPackages: () => WarrantyPackagesResponse | null;
  getReadableDiscountRecordPrice: (discountRecord: DiscountRecords) => string;
  getHumanReadableOrderSubtotal: () => string;
  getHumanReadableOrderTotal: () => string;
  getHumanReadableOrderTotalPaid: () => string;
  getHumanReadableTotalRemaining: () => string;
  getTotalRemainingAmount: () => number;
  getHumanReadableOrderTotalWithDiscount: () => string;
  getRawSelfAssemblyDiscount: () => number;
  getRawSelfAssemblyDeliveryPrice: () => number;
  getTotalPayableAmount: () => string;
  getOrderExtraDiscounts: () => number | undefined;
  getVoucherDiscount: () => number;
  hasVouchersAlready?: boolean;
}

const splitReference = (ref: string | null | undefined) => {
  if (!ref) return "";

  if (ref.includes(" ")) {
    const strings = ref.split(" ");
    return strings[0];
  }

  return ref;
};

// TODO: Extract warranty logic and order logic to hooks, order provider should only be used as a store
const OrderContext = createContext({} as OrderProviderProps);
export const useOrderContext = () => useContext(OrderContext);
const OrderProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get(APPOINTMENT_TYPE_SEARCH_PARAM) || "";
  const [orderReference, setOrderReference] = useState("");
  const [orderId, setOrderId] = useState("");
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [warrantyPackage, setWarrantyPackage] = useState<
    WarrantyPackage | undefined
  >(undefined);
  const [warrantyDiscount, setWarrantyDiscount] = useState<number>(0);
  const [remaining, setRemaining] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const [orderStatusId, setOrderStatusId] = useState(0);

  const { enableWarrantyOrPayment } = useQueryGates();

  const { warranties, warrantyPackagesDataRequest } = useWarrantyData(
    enableWarrantyOrPayment,
  );

  const { voucher, orderWarrantyPackages } = useSnapshot(voucherState);
  const voucherWarranties = voucher?.warrantyPackages;

  const { voucherWarrantyPackage } = useWarrantyOrderVoucherData({
    warrantyPackages: orderWarrantyPackages as readonly WarrantyPackage[],
    voucherWarranties,
  });

  useEffect(() => {
    const reference = searchParams.get("reference") || "";
    const id = searchParams.get("orderId") || "";
    const orderStatusId = JSON.parse(searchParams.get("orderStatusId") || "0");

    if (!reference && id) {
      setOrderReference(splitReference(id));
    } else if (reference && !id) {
      setOrderReference(reference);
    } else {
      setOrderReference(reference);
    }

    if (id) {
      setOrderId(id);
    }

    if (orderStatusId) {
      setOrderStatusId(orderStatusId);
    }
  }, [searchParams]);

  const { order, orderError, isOrderFetching, refetchOrder } = useOrder(
    orderReference,
    type,
  );

  useEffect(() => {
    if (!order) return;
    if (order.warrantyPackage) {
      setWarrantyPackage(order.warrantyPackage);
    }
  }, [order]);

  const getFormattedSubTotalPrice = () => {
    return getFormattedPrice(getOrderSubtotal());
  };

  const getOrderSubtotal = () => {
    if (!order) return 0;

    let subTotal = 0;

    for (const product of order.products) {
      subTotal += product.totalPrice;
    }

    if (order.supplyProducts) {
      for (const supplyProduct of order.supplyProducts) {
        subTotal += supplyProduct.price;
      }
    }

    return subTotal;
  };

  const getFormattedWarrantyPackagePrice = () => {
    return getFormattedPrice(order?.warrantyPackage?.price);
  };

  const getRawAbsoluteDiscountPrice = (): number => {
    if (!order) return 0;

    let discountPrice = 0;
    if (order.products.length > 1) {
      discountPrice = (order.products.length - 1) * 50;
    }
    return discountPrice;
  };

  const getVoucherDiscount = (): number => {
    if (!voucher || !order) return 0;

    const extraDiscounts = order?.extraDiscounts ?? 0;
    const basePrice = voucherWarrantyPackage
      ? voucherWarrantyPackage.price
      : Math.max(
          0,
          getOrderSubtotal() + getWarrantyPackageTotal() - extraDiscounts,
        );

    const { type, value } = voucher;

    if (type === "abs") {
      // absolute amount; never more than base or below 0
      return value;
    }

    if (type === "rel") {
      // relative percent; value is percentage (e.g., 15 → 15%)
      const pct = value / 100;
      return clamp(basePrice * pct, 0, basePrice);
    }

    // Unknown voucher type
    return 0;
  };

  const getRawSelfAssemblyDiscount = (): number => {
    if (!order) return 0;

    return order.products.reduce(
      (discount, product) => discount + (product?.selfAssemblyDiscount || 0),
      0,
    );
  };

  const getFormattedAbsoluteDiscountPrice = (): string => {
    return getFormattedPrice(
      getRawAbsoluteDiscountPrice(),
      !!getRawAbsoluteDiscountPrice(),
    );
  };

  const getWarrantyPackageTotal = (): number => {
    if (!warrantyPackage) return 0;
    return warrantyPackage?.price;
  };

  const getWarrantyOwnRiskTotal = (): number => {
    if (!order?.warrantyOwnRiskPrice) return 0;
    return order?.warrantyOwnRiskPrice;
  };

  const getReadableDiscountRecordPrice = (discountRecord: DiscountRecords) => {
    if (discountRecord.type === "rel") {
      return `-${discountRecord.value}%`;
    } else {
      return getFormattedPrice(
        getRawOrderDiscountRecordPrice(discountRecord),
        true,
      );
    }
  };

  const getWarrantyPackageDiscount = (method: string): number => {
    if (!warrantyPackage) return 0;

    if (method === "PAY_NOW" || getOrderTotalPaid() > 0) return 0;

    if (
      warrantyPackage.packageId ===
        warranties.decideLaterWarrantyPackage.packageId ||
      warrantyPackage.packageId ===
        warranties.noWarrantyPayNowPackage.packageId ||
      warrantyPackage.packageId ===
        warranties.noWarrantyPayFullPackage.packageId
    ) {
      return 0;
    }

    const freeWarrantyPackage = warrantyPackagesDataRequest.data
      ? warrantyPackagesDataRequest.data.freeWarrantyPackage
      : undefined;

    return freeWarrantyPackage ? freeWarrantyPackage.price : 0;
  };

  const getOrderWarrantyPackages = () => {
    return warrantyPackagesDataRequest.data || null;
  };

  const getOrderExtraDiscounts = () => {
    return order?.extraDiscounts;
  };

  const getRawTotalPrice = () => {
    if (!order) return 0;
    if (
      order.paymentData.total &&
      !order.shoppingCart?.sampleSale &&
      !order.discountRecords
    )
      return order.paymentData.total;
    return (
      getOrderSubtotal() + getWarrantyPackageTotal() + getWarrantyOwnRiskTotal()
    );
  };

  const getRawOrderDiscountRecordsPrice = () => {
    let discountTotal = 0;

    if (order?.discountRecords) {
      for (const discountRecord of order.discountRecords) {
        discountTotal += getRawOrderDiscountRecordPrice(discountRecord);
      }
    }
    return discountTotal;
  };

  const getRawOrderDiscountRecordPrice = (discountRecord: DiscountRecords) => {
    let discountTotal = 0;

    if (discountRecord.type === "abs") {
      discountTotal += discountRecord.value;
    } else if (discountRecord.type === "rel") {
      discountTotal += getRawTotalPrice() * (discountRecord.value / 100);
    }

    return discountTotal;
  };

  const getRawSelfAssemblyDeliveryPrice = () => {
    if (order && order.isSelfAssembly) return DELIVERY_PRICES.SELF_ASSEMBLY;
    return 0;
  };

  const getOrderTotalWithDiscount = () => {
    if (!order) return 0;
    return getRawTotalPrice() - getRawOrderDiscountRecordsPrice();
  };

  const getOrderTotalRemaining = () => {
    return getOrderTotalWithDiscount() - getOrderTotalPaid();
  };

  const getOrderTotalPaid = () => {
    if (!order) return 0;

    return order?.paymentData.paid + order?.paymentData.supplyPaid;
  };

  const getTotalPrice = () => {
    let warrantyPrice = order?.warrantyPackage ? 0 : getWarrantyPackageTotal();
    const voucherDiscount = getVoucherDiscount();
    const totalPrice =
      (order?.paymentData.total ?? 0) + warrantyPrice - voucherDiscount;
    return getFormattedPrice(totalPrice);
  };

  const getHumanReadableOrderSubtotal = () => {
    return getFormattedPrice(getOrderSubtotal());
  };

  const getHumanReadableOrderTotal = () => {
    return getFormattedPrice(getOrderTotalWithDiscount());
  };

  const getHumanReadableOrderTotalPaid = () => {
    return getFormattedPrice(getOrderTotalPaid());
  };

  // Calculates the remaining amount to be paid for the order
  // Nu te betalen
  const getTotalRemainingAmount = () => {
    // If order doesnt exist => 0
    if (!order || !order.paymentData) return 0;

    // If there are discount records, return the total discounted price calculation
    if ((order.discountRecords || voucher) && !order.paymentData.paid) {
      let warrantyPrice = order?.warrantyPackage
        ? 0
        : getWarrantyPackageTotal();
      const voucherDiscount = getVoucherDiscount();
      const totalPrice =
        (order?.paymentData.total ?? 0) + warrantyPrice - voucherDiscount;
      return totalPrice;
    }

    // Return the total amount subtracted by the paid amount
    let payableAmount = order?.paymentData.total - order?.paymentData.paid;
    // If there is no warranty package already added from earlier,
    // Add the currently selected warranty package to the total price
    if (!order.warrantyPackage) {
      payableAmount += getWarrantyPackageTotal();
    }
    return payableAmount;
  };

  const getHumanReadableTotalRemaining = () => {
    const payableAmount = getTotalRemainingAmount();

    return getFormattedPrice(payableAmount);
  };

  const getHumanReadableOrderTotalWithDiscount = () => {
    return getFormattedPrice(getOrderTotalWithDiscount());
  };

  const getTotalPayableAmount = () => {
    return getFormattedPrice(order?.paymentData.total);
  };

  const hasVouchersAlready =
    order &&
    Array.isArray(order.discountRecords) &&
    order.discountRecords.some(
      (record: DiscountRecords) =>
        (record.type === "abs" || record.type === "rel") &&
        record.id !== "extraDoors",
    );

  const isOrderAssembly = order?.isSelfAssembly;

  return (
    <OrderContext.Provider
      value={{
        type,
        order,
        orderReference,
        orderError,
        isOrderFetching,
        setOrderReference,
        refetchOrder,
        orderId,
        warrantyPackage,
        setWarrantyPackage,
        getFormattedSubTotalPrice,
        getFormattedWarrantyPackagePrice,
        getFormattedAbsoluteDiscountPrice,
        getTotalPrice,
        getRawTotalPrice,
        monthlyPayment,
        setMonthlyPayment,
        getOrderSubtotal,
        getWarrantyPackageTotal,
        getRawOrderDiscountRecordsPrice,
        remaining,
        setRemaining,
        getOrderTotalRemaining,
        getOrderTotalPaid,
        warrantyDiscount,
        setWarrantyDiscount,
        getWarrantyPackageDiscount,
        getWarrantyOwnRiskTotal,
        total,
        setTotal,
        getOrderWarrantyPackages,
        getReadableDiscountRecordPrice,
        orderStatusId,
        getHumanReadableOrderSubtotal,
        getHumanReadableOrderTotal,
        getHumanReadableOrderTotalPaid,
        getHumanReadableTotalRemaining,
        getTotalRemainingAmount,
        getHumanReadableOrderTotalWithDiscount,
        getRawSelfAssemblyDiscount,
        getRawSelfAssemblyDeliveryPrice,
        isOrderAssembly,
        getTotalPayableAmount,
        getOrderExtraDiscounts,
        getVoucherDiscount,
        hasVouchersAlready,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
export default OrderProvider;
